import React from "react";
import { fmtBRL, fmtNumber, fmtDateShort } from "../../lib/format";
import type { CalculationMemoryGroupDetailsDto } from "@bi/types";

function fmtValue(v: number, unit: "BRL" | "number" | undefined): string {
  if (unit === "number") return fmtNumber(v);
  return fmtBRL(v);
}

// ── sub-componente: tabela de duplicatas ──────────────────────────────────────

export function DetailTable({
  data,
  groupKey,
  detailPage,
  setDetailPage,
  limit,
  displayUnit,
}: {
  data: CalculationMemoryGroupDetailsDto | undefined;
  groupKey: string;
  detailPage: Record<string, number>;
  setDetailPage: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  limit: number;
  displayUnit?: "BRL" | "number";
}) {
  if (!data) {
    return <p className="text-xs text-gray-400 py-2">Carregando registros…</p>;
  }

  const totalPages = Math.ceil(data.total / limit);
  const curPage = detailPage[groupKey] ?? 0;
  const isMovement = data.detailType === "movement";
  const isBalance = data.detailType === "balance";
  const isProjection = data.detailType === "projection";
  // Drill de grupo econômico: cada nota traz a filial (nome + CNPJ). Mostra a coluna
  // só quando há filial, para dar pra "ver as notas agrupadas por CNPJ/filial".
  const hasFilial = !isMovement && !isBalance && !isProjection && data.rows.some((r) => r.counterpartyName);

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-gray-200 text-gray-400">
            <th className="py-1.5 text-left font-medium">
              {isBalance ? "Conta" : isProjection ? "Título" : "Chave"}
            </th>
            {isMovement ? (
              <>
                <th className="py-1.5 text-left font-medium">Data</th>
                <th className="py-1.5 text-left font-medium">Forma</th>
                <th className="py-1.5 text-right font-medium">Valor</th>
              </>
            ) : isBalance ? (
              <>
                <th className="py-1.5 text-left font-medium">Ag / Conta</th>
                <th className="py-1.5 text-right font-medium">Saldo</th>
              </>
            ) : isProjection ? (
              <>
                <th className="py-1.5 text-left font-medium">Tipo</th>
                <th className="py-1.5 text-left font-medium">Contraparte</th>
                <th className="py-1.5 text-left font-medium">Vencimento</th>
                <th className="py-1.5 text-right font-medium">Valor (± no gap)</th>
              </>
            ) : (
              <>
                {hasFilial && <th className="py-1.5 text-left font-medium">Filial</th>}
                <th className="py-1.5 text-left font-medium">Emissão</th>
                <th className="py-1.5 text-left font-medium">Vencimento</th>
                <th className="py-1.5 text-right font-medium">Valor Original</th>
                <th className="py-1.5 text-right font-medium">Liquidado</th>
                <th className="py-1.5 text-right font-medium">Saldo</th>
                <th className="py-1.5 text-right font-medium">Dias Atraso</th>
              </>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data.rows.map((r) => (
            <tr key={r.externalKey} className="text-gray-700">
              <td className="py-1.5 font-mono text-gray-400 max-w-[180px] truncate">
                {isBalance ? r.externalKey.substring(0, 8) + "…" : r.externalKey}
              </td>
              {isMovement ? (
                <>
                  <td className="py-1.5">{fmtDateShort(r.dueDate)}</td>
                  <td className="py-1.5 text-gray-500">{r.statusName}</td>
                  <td className="py-1.5 text-right font-medium">{fmtValue(r.openAmount, displayUnit)}</td>
                </>
              ) : isBalance ? (
                <>
                  <td className="py-1.5 text-gray-500">{r.statusName}</td>
                  <td className={`py-1.5 text-right font-medium ${r.openAmount < 0 ? "text-red-600" : "text-gray-900"}`}>
                    {fmtBRL(r.openAmount)}
                  </td>
                </>
              ) : isProjection ? (
                <>
                  <td className={`py-1.5 ${r.openAmount >= 0 ? "text-green-700" : "text-amber-700"}`}>
                    {r.statusName}
                  </td>
                  <td className="py-1.5 text-gray-600 max-w-[160px] truncate">{r.counterpartyName ?? "—"}</td>
                  <td className="py-1.5">{fmtDateShort(r.dueDate)}</td>
                  <td className={`py-1.5 text-right font-medium ${r.openAmount >= 0 ? "text-green-700" : "text-red-600"}`}>
                    {fmtBRL(r.openAmount)}
                  </td>
                </>
              ) : (
                <>
                  {hasFilial && (
                    <td className="py-1.5 text-gray-600 max-w-[220px] truncate" title={r.counterpartyName ?? ""}>
                      {r.counterpartyName ?? "—"}
                    </td>
                  )}
                  <td className="py-1.5">{fmtDateShort(r.issueDate)}</td>
                  <td className="py-1.5">{fmtDateShort(r.dueDate)}</td>
                  <td className="py-1.5 text-right">{fmtValue(r.invoiceAmount, displayUnit)}</td>
                  <td className="py-1.5 text-right text-gray-500">
                    {r.settledAmount != null ? fmtValue(r.settledAmount, displayUnit) : "—"}
                  </td>
                  <td className="py-1.5 text-right font-medium">{fmtValue(r.openAmount, displayUnit)}</td>
                  <td className={`py-1.5 text-right ${r.daysOverdue > 0 ? "text-red-600 font-medium" : "text-gray-400"}`}>
                    {r.daysOverdue > 0 ? r.daysOverdue : "—"}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {totalPages > 1 && (
        <div className="flex justify-end gap-2">
          <button
            className="text-xs text-gray-400 hover:text-gray-700 disabled:opacity-30"
            disabled={curPage === 0}
            onClick={() => setDetailPage((p) => ({ ...p, [groupKey]: curPage - 1 }))}
          >
            ← Anterior
          </button>
          <span className="text-xs text-gray-400">
            {curPage + 1}/{totalPages}
          </span>
          <button
            className="text-xs text-gray-400 hover:text-gray-700 disabled:opacity-30"
            disabled={curPage >= totalPages - 1}
            onClick={() => setDetailPage((p) => ({ ...p, [groupKey]: curPage + 1 }))}
          >
            Próximo →
          </button>
        </div>
      )}
    </div>
  );
}

export function fmtDocument(doc: string): string {
  const d = doc.replace(/\D/g, "");
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  return doc;
}
